import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Button, ButtonText, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import { ShieldCheck } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { MeetingChrome } from '@/common/components/meeting/MeetingChrome';
import {
  MeetingConfirmProvider,
  useMeetingConfirm,
} from '@/common/components/meeting/MeetingConfirm';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { isSelfServiceRun, usesOrientationFilm } from '@/common/utils/oralExam/runConduct';
import type { OralExamLoungeHint } from '@/student/module/utils/oralExamLounge';
import { OralExamPracticeBanner } from '@/student/module/components/OralExamPracticeBanner';

import { LobbyConsentStep } from './LobbyConsentStep';
import { LobbyConductStep } from './LobbyConductStep';
import { LobbyDeviceStep } from './LobbyDeviceStep';
import { LobbyIdentityStep } from './LobbyIdentityStep';
import { LobbyOrientationStep } from './LobbyOrientationStep';
import { LobbyScreenShareStep } from './LobbyScreenShareStep';
import { LobbyStepper } from './LobbyStepper';
import { LobbyWaitingStep } from './LobbyWaitingStep';
import { LobbyWelcomeStep } from './LobbyWelcomeStep';
import {
  LOBBY_STEPS,
  type LobbyStepActions,
  type LobbyStepDescriptor,
  type LobbyStepKey,
  type LobbyStepProps,
} from './types';

export type OralExamLobbyProps = {
  runState: OralExamRunStateResponse;
  onRecordConsent: () => void;
  onAcknowledgeConduct: () => void;
  onSubmitIdentityPhotos: (documentImageBase64: string, selfieImageBase64: string) => Promise<void>;
  isRecordingConsent?: boolean;
  isAcknowledgingConduct?: boolean;
  isSubmittingIdentityPhotos?: boolean;
  /** Stamp server lobby-ready when the student reaches orientation or waiting. */
  onMarkPreflightLobbyReady?: (etag?: string | null) => void;
  /** Leave the meeting (surfaced inside MeetingChrome — the single nav bar). */
  onBack?: () => void;
  loungeHints?: OralExamLoungeHint[];
  useProductDefaultHints?: boolean;
  orientationVideoUrl?: string | null;
  loungeOrientationResolved?: boolean;
  loungeFetchFailed?: boolean;
  onOrientationComplete?: () => void | Promise<void>;
  isCompletingOrientation?: boolean;
  orientationStampRetrying?: boolean;
  orientationStampExhausted?: boolean;
  onActiveStepChange?: (key: LobbyStepKey) => void;
  slotStartIso?: string | null;
  onStartPractice?: () => void | Promise<void>;
  isStartingPractice?: boolean;
  startPracticeError?: string | null;
};

const STEP_COMPONENTS: Record<LobbyStepKey, React.ComponentType<LobbyStepProps>> = {
  device: LobbyDeviceStep,
  consent: LobbyConsentStep,
  conduct: LobbyConductStep,
  screenShare: LobbyScreenShareStep,
  identity: LobbyIdentityStep,
  orientation: LobbyOrientationStep,
  waiting: LobbyWaitingStep,
};

function hasSetupProgress(runState: OralExamRunStateResponse): boolean {
  return Boolean(
    runState.consentRecord
    || runState.conductAcknowledgedAt
    || runState.idPhotoBlobPath
    || runState.idSelfiePhotoBlobPath,
  );
}

function isOrientationEnabled(runState: OralExamRunStateResponse): boolean {
  const idStatus = runState.idVerification?.status;
  return usesOrientationFilm(runState)
    && runState.sessionPhase === OralExamSessionPhase.pre_flight
    && (idStatus === 'submitted' || idStatus === 'approved');
}

/**
 * The unified oral-exam Lobby shell: nav chrome, quiet header, left
 * {@link LobbyStepper}, and the active panel. Step ⓪ welcome is a leading phase
 * outside LOBBY_STEPS; the numbered steps start after "Begin set-up".
 */
function OralExamLobbyInner({
  runState,
  onRecordConsent,
  onAcknowledgeConduct,
  onSubmitIdentityPhotos,
  isRecordingConsent = false,
  isAcknowledgingConduct = false,
  isSubmittingIdentityPhotos = false,
  onMarkPreflightLobbyReady,
  onBack,
  loungeHints,
  useProductDefaultHints,
  orientationVideoUrl,
  loungeOrientationResolved = false,
  loungeFetchFailed = false,
  onOrientationComplete,
  isCompletingOrientation,
  orientationStampRetrying = false,
  orientationStampExhausted = false,
  onActiveStepChange,
  slotStartIso,
  onStartPractice,
  isStartingPractice = false,
  startPracticeError = null,
}: OralExamLobbyProps) {
  const { t } = useTranslation();
  const { confirm } = useMeetingConfirm();
  const gradedLobby = !isSelfServiceRun(runState);

  const [setupStarted, setSetupStarted] = useState(() => hasSetupProgress(runState));
  const [orientationReached, setOrientationReached] = useState(false);
  const [orientationRejectLatch, setOrientationRejectLatch] = useState(false);
  const [orientationRejectChoice, setOrientationRejectChoice] = useState<'fixNow' | 'finishFirst' | null>(null);
  const [orientationRejectLatchCleared, setOrientationRejectLatchCleared] = useState(false);
  const [orientationPausedAtSeconds, setOrientationPausedAtSeconds] = useState<number | null>(null);
  const [orientationLocallyCompleted, setOrientationLocallyCompleted] = useState(false);

  const [localDone, setLocalDone] = useState<Record<LobbyStepKey, boolean>>({
    device: false,
    consent: false,
    conduct: false,
    screenShare: false,
    identity: false,
    orientation: false,
    waiting: false,
  });

  const lastOrientationVideoUrlRef = useRef<string | null | undefined>(undefined);
  const loungeOrientationResolvedRef = useRef(false);

  const orientationEnabled = isOrientationEnabled(runState);
  if (loungeOrientationResolved) {
    loungeOrientationResolvedRef.current = true;
  }
  let resolvedOrientationVideoUrl: string | null | undefined;
  if (orientationVideoUrl === null) {
    resolvedOrientationVideoUrl = null;
  } else if (typeof orientationVideoUrl === 'string' && orientationVideoUrl.length > 0) {
    lastOrientationVideoUrlRef.current = orientationVideoUrl;
    resolvedOrientationVideoUrl = orientationVideoUrl;
  } else {
    resolvedOrientationVideoUrl = lastOrientationVideoUrlRef.current;
  }
  const resolvedLoungeOrientation = loungeOrientationResolved || loungeOrientationResolvedRef.current;
  const confirmedNoFilm = resolvedLoungeOrientation && resolvedOrientationVideoUrl === null;
  const orientationAlreadyDone = Boolean(runState.orientationCompletedAt) || orientationLocallyCompleted;
  const orientationFilmPending = !confirmedNoFilm && !orientationAlreadyDone;
  // Reject clears blob paths in the same persist. Latch on having reached
  // orientation this visit, not on live photo fields (those are already gone).
  const rejectMidOrientationFilm = runState.idVerification?.status === 'rejected'
    && orientationFilmPending
    && orientationReached
    && !orientationRejectLatchCleared;
  const effectiveRejectLatch = orientationRejectLatch || rejectMidOrientationFilm;
  const orientationStepVisible = orientationFilmPending
    && (orientationEnabled || orientationReached || effectiveRejectLatch);

  const autoStampAttemptedRef = useRef(false);
  const exhaustedPersistRetryRef = useRef(false);
  const onOrientationCompleteRef = useRef(onOrientationComplete);
  onOrientationCompleteRef.current = onOrientationComplete;

  // Parent often passes an inline callback; keep a stable ref so the lobby-ready
  // stamp effect does not re-fire every render.
  const onMarkPreflightLobbyReadyRef = useRef(onMarkPreflightLobbyReady);
  onMarkPreflightLobbyReadyRef.current = onMarkPreflightLobbyReady;
  // Only one attempt per ETag — avoids a 412 storm when If-Match is stale.
  const lastLobbyReadyEtagRef = useRef<string | null>(null);
  const prevIdVerificationStatusRef = useRef(runState.idVerification?.status);

  const isStepDone = useCallback((key: LobbyStepKey): boolean => {
    switch (key) {
      case 'consent':
        return Boolean(runState.consentRecord);
      case 'conduct':
        return Boolean(runState.conductAcknowledgedAt);
      case 'identity':
        if (runState.idVerification?.status === 'rejected') return false;
        return runState.idVerification?.status === 'submitted'
          || runState.idVerification?.status === 'approved';
      case 'orientation':
        if (effectiveRejectLatch) return false;
        if (!orientationEnabled) return true;
        if (!resolvedLoungeOrientation) return false;
        if (resolvedOrientationVideoUrl === null) return true;
        return orientationAlreadyDone;
      case 'waiting':
        return false;
      default:
        return localDone[key];
    }
  }, [
    localDone,
    orientationAlreadyDone,
    effectiveRejectLatch,
    orientationEnabled,
    resolvedOrientationVideoUrl,
    resolvedLoungeOrientation,
    runState.consentRecord,
    runState.conductAcknowledgedAt,
    runState.idVerification?.status,
  ]);

  const derivedActiveKey: LobbyStepKey = useMemo(
    () => LOBBY_STEPS.find((step) => {
      if (step.key === 'orientation' && !orientationStepVisible) return false;
      return !isStepDone(step.key);
    })?.key ?? 'waiting',
    [isStepDone, orientationStepVisible],
  );

  const activeKey: LobbyStepKey = useMemo(() => {
    if (effectiveRejectLatch) {
      return 'orientation';
    }
    return derivedActiveKey;
  }, [derivedActiveKey, effectiveRejectLatch]);

  useEffect(() => {
    if (derivedActiveKey === 'orientation') {
      setOrientationReached(true);
    }
  }, [derivedActiveKey]);

  useEffect(() => {
    if (rejectMidOrientationFilm) {
      setOrientationRejectLatch(true);
    }
  }, [rejectMidOrientationFilm]);

  useEffect(() => {
    const prevStatus = prevIdVerificationStatusRef.current;
    prevIdVerificationStatusRef.current = runState.idVerification?.status;
    if (runState.idVerification?.status === 'submitted' && prevStatus === 'rejected') {
      setOrientationRejectLatchCleared(false);
      setOrientationRejectLatch(false);
      setOrientationRejectChoice(null);
    }
  }, [runState.idVerification?.status]);

  useEffect(() => {
    if (setupStarted) {
      onActiveStepChange?.(activeKey);
    }
  }, [activeKey, onActiveStepChange, setupStarted]);

  useEffect(() => {
    autoStampAttemptedRef.current = false;
    exhaustedPersistRetryRef.current = false;
  }, [runState.idVerification?.status]);

  useEffect(() => {
    if (!confirmedNoFilm || orientationAlreadyDone) return undefined;
    if (autoStampAttemptedRef.current) return undefined;
    autoStampAttemptedRef.current = true;
    Promise.resolve(onOrientationCompleteRef.current?.()).catch(() => undefined);
    return undefined;
  }, [confirmedNoFilm, orientationAlreadyDone]);

  useEffect(() => {
    if (!orientationStampExhausted || runState.orientationCompletedAt) return;
    if (exhaustedPersistRetryRef.current) return;
    if (!confirmedNoFilm && !orientationLocallyCompleted) return;
    exhaustedPersistRetryRef.current = true;
    autoStampAttemptedRef.current = false;
    setOrientationLocallyCompleted(true);
  }, [
    confirmedNoFilm,
    orientationLocallyCompleted,
    orientationStampExhausted,
    runState.orientationCompletedAt,
  ]);

  useEffect(() => {
    if (!orientationLocallyCompleted || runState.orientationCompletedAt) return;
    const idStatus = runState.idVerification?.status;
    if (idStatus !== 'submitted' && idStatus !== 'approved') return;
    if (autoStampAttemptedRef.current) return;
    autoStampAttemptedRef.current = true;
    Promise.resolve(onOrientationCompleteRef.current?.()).catch(() => undefined);
  }, [orientationLocallyCompleted, runState.orientationCompletedAt, runState.idVerification?.status]);

  const steps: LobbyStepDescriptor[] = useMemo(
    () => LOBBY_STEPS.filter((step) => step.key !== 'orientation' || orientationStepVisible).map((step) => {
      let status: LobbyStepDescriptor['status'];
      if (!setupStarted) {
        status = 'upcoming';
      } else if (
        step.key === 'identity'
        && effectiveRejectLatch
        && runState.idVerification?.status === 'rejected'
      ) {
        status = 'attention';
      } else if (isStepDone(step.key)) {
        status = 'done';
      } else if (step.key === activeKey) {
        status = 'active';
      } else {
        status = 'upcoming';
      }
      return {
        key: step.key,
        label: t(step.labelKey),
        status,
      };
    }),
    [
      t,
      isStepDone,
      activeKey,
      setupStarted,
      orientationStepVisible,
      effectiveRejectLatch,
      runState.idVerification?.status,
    ],
  );

  const handleComplete = useCallback(() => {
    // Consent, conduct, identity, and orientation doneness come from polled runState.
    if (activeKey === 'consent' || activeKey === 'conduct' || activeKey === 'identity' || activeKey === 'orientation') return;
    setLocalDone((prev) => ({ ...prev, [activeKey]: true }));
  }, [activeKey]);

  useEffect(() => {
    const onLobbyReadyStep = activeKey === 'orientation' || activeKey === 'waiting';
    if (!setupStarted || !onLobbyReadyStep) {
      lastLobbyReadyEtagRef.current = null;
      return;
    }
    if (runState.preflightLobbyReadyAt) return;
    const etag = runState.etag ?? null;
    if (!etag) return;
    if (lastLobbyReadyEtagRef.current === etag) return;
    lastLobbyReadyEtagRef.current = etag;
    onMarkPreflightLobbyReadyRef.current?.(etag);
  }, [setupStarted, activeKey, runState.preflightLobbyReadyAt, runState.etag]);

  const actions: LobbyStepActions = useMemo(() => ({
    recordConsent: onRecordConsent,
    acknowledgeConduct: onAcknowledgeConduct,
    submitIdentityPhotos: onSubmitIdentityPhotos,
    isRecordingConsent,
    isAcknowledgingConduct,
    isSubmittingIdentityPhotos,
  }), [
    onRecordConsent,
    onAcknowledgeConduct,
    onSubmitIdentityPhotos,
    isRecordingConsent,
    isAcknowledgingConduct,
    isSubmittingIdentityPhotos,
  ]);

  const handleOrientationRejectFixNow = useCallback(() => {
    setOrientationRejectLatchCleared(true);
    setOrientationRejectLatch(false);
    setOrientationRejectChoice(null);
  }, []);

  const handleOrientationRejectFinishFirst = useCallback(() => {
    setOrientationRejectChoice('finishFirst');
  }, []);

  const handleOrientationPauseOffset = useCallback((seconds: number) => {
    setOrientationPausedAtSeconds(seconds);
  }, []);

  const handleOrientationCompleteWithRetake = useCallback(async () => {
    const finishFirst = orientationRejectChoice === 'finishFirst';
    const idStatus = runState.idVerification?.status;
    const canStamp = idStatus === 'submitted' || idStatus === 'approved';
    // The API rejects the stamp while ID is rejected (OA-15). Finish-first skip/end
    // must still count as watched locally so resubmit goes to waiting, not the film.
    if (canStamp) {
      await onOrientationComplete?.();
    }
    if (finishFirst) {
      setOrientationLocallyCompleted(true);
      setOrientationRejectLatch(false);
      setOrientationRejectChoice(null);
      setOrientationRejectLatchCleared(true);
    }
  }, [onOrientationComplete, orientationRejectChoice, runState.idVerification?.status]);

  const handleOrientationContinueAnyway = useCallback(() => {
    setOrientationLocallyCompleted(true);
    if (orientationRejectChoice === 'finishFirst') {
      setOrientationRejectLatch(false);
      setOrientationRejectChoice(null);
      setOrientationRejectLatchCleared(true);
    }
  }, [orientationRejectChoice]);

  const handleLeaveRequest = useCallback(async () => {
    if (!onBack) return;
    if (!gradedLobby) {
      await Promise.resolve(onBack());
      return;
    }
    const confirmed = await confirm({
      title: t('oralExam.lobby.waiting.leaveConfirm.title'),
      message: t('oralExam.lobby.waiting.leaveConfirm.message'),
      confirmLabel: t('oralExam.lobby.waiting.leaveConfirm.confirm'),
      cancelLabel: t('oralExam.lobby.waiting.leaveConfirm.cancel'),
      destructive: true,
      overlayTestID: 'oral-exam-lobby-leave-confirm',
      messageTestID: 'oral-exam-lobby-leave-confirm-message',
    });
    if (confirmed) {
      await Promise.resolve(onBack());
    }
  }, [confirm, gradedLobby, onBack, t]);

  const stepProps: LobbyStepProps = {
    runState,
    onComplete: handleComplete,
    onLeave: onBack ? handleLeaveRequest : undefined,
    actions,
    loungeHints,
    useProductDefaultHints,
    orientationVideoUrl: resolvedOrientationVideoUrl,
    loungeOrientationResolved: resolvedLoungeOrientation,
    loungeFetchFailed,
    onOrientationComplete: handleOrientationCompleteWithRetake,
    isCompletingOrientation,
    orientationStampRetrying,
    orientationStampExhausted,
    onOrientationContinueAnyway: handleOrientationContinueAnyway,
    slotStartIso,
    orientationRejectLatch: effectiveRejectLatch,
    orientationRejectChoice,
    orientationPausedAtSeconds,
    onOrientationRejectFixNow: handleOrientationRejectFixNow,
    onOrientationRejectFinishFirst: handleOrientationRejectFinishFirst,
    onOrientationPauseOffset: handleOrientationPauseOffset,
    onStartPractice,
    isStartingPractice,
    startPracticeError,
  };

  const ActiveStep = STEP_COMPONENTS[activeKey];
  const isWaitingHold = setupStarted && activeKey === 'waiting';

  return (
    <MeetingChrome onBack={onBack ? handleLeaveRequest : undefined}>
      <VStack flex={1} bg="$bgApp" testID="oral-exam-lobby">
        {isSelfServiceRun(runState) ? <OralExamPracticeBanner /> : null}
        {isWaitingHold ? null : (
          <HStack
            alignItems="center"
            space="sm"
            px="$6"
            py="$4"
            bg="$bgBrandSubtle"
            borderBottomWidth={1}
            borderBottomColor="$borderLight"
            testID="oral-exam-lobby-header"
          >
            <Icon as={ShieldCheck} size="md" color="$brandBase" />
            <Text size="md" fontWeight="semibold" color="$textHeading">
              {t('oralExam.lobby.header')}
            </Text>
          </HStack>
        )}

        <Box flex={1}>
          {isWaitingHold ? (
            <Box
              flex={1}
              bg="$bgApp"
              testID="oral-exam-lobby-active-panel"
            >
              <ActiveStep {...stepProps} />
            </Box>
          ) : (
            <HStack flex={1} space="xl" p="$6" maxWidth={1000}>
              <Box width={220} flexShrink={0}>
                <LobbyStepper steps={steps} />
              </Box>
              <Box
                flex={1}
                bg="$bgCard"
                borderWidth={1}
                borderColor="$borderLight"
                borderRadius="$lg"
                overflow="scroll"
                sx={{
                  _web: {
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarGutter: 'auto',
                  },
                }}
                testID="oral-exam-lobby-active-panel"
              >
                {!setupStarted ? (
                  <LobbyWelcomeStep
                    onComplete={() => setSetupStarted(true)}
                    selfService={isSelfServiceRun(runState)}
                  />
                ) : (
                  <ActiveStep {...stepProps} />
                )}
              </Box>
            </HStack>
          )}
        </Box>

        {onBack ? (
          <HStack
            justifyContent="flex-end"
            px="$6"
            py="$3"
            borderTopWidth={1}
            borderTopColor="$borderLight"
          >
            <Button
              variant="outline"
              size="sm"
              action="secondary"
              onPress={() => {
                handleLeaveRequest().catch(() => undefined);
              }}
              testID="oral-exam-lobby-leave"
            >
              <ButtonText>{t('oralExam.leaveExam')}</ButtonText>
            </Button>
          </HStack>
        ) : null}
      </VStack>
    </MeetingChrome>
  );
}

/** Lobby shell with in-tree leave confirmation (student session is outside lecturer MeetingRoom). */
export function OralExamLobby(props: OralExamLobbyProps) {
  return (
    <MeetingConfirmProvider>
      <OralExamLobbyInner {...props} />
    </MeetingConfirmProvider>
  );
}
